import { useEffect, useState } from 'react';

/**
 * Viewport width below which box-os switches to its mobile presentation.
 * Matches Tailwind's `md` breakpoint so runtime branches and `md:` utility
 * classes always agree — if these ever diverge you get layouts that are half
 * mobile and half desktop at the boundary.
 */
export const MOBILE_BREAKPOINT_PX = 768;

/**
 * Subscribes to a media query and returns whether it currently matches.
 *
 * Reads `window.matchMedia` during the initial state computation rather than in
 * an effect, so the first paint is already correct — an effect-only read makes
 * every mobile view flash its desktop layout for one frame.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    // Re-sync on mount: the query may have changed between render and effect.
    setMatches(mql.matches);

    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * True when the viewport is narrower than the mobile breakpoint.
 *
 * Use this only for branches that CSS can't express — swapping a side sheet for
 * a bottom drawer, or the desktop calendar timeline for the touch grid. For
 * anything that's purely visual reflow, prefer Tailwind's `md:` classes: they
 * cost no JS and don't re-render on resize.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
}
